import React, { RefObject } from 'react';
import { MRT_ColumnDef, MRT_Row } from 'material-react-table';
import { Box, TextField } from '@mui/material';
import { DimensionAllocation, Site } from "api";
import { COLORS } from 'components';
import { useNotify } from 'hooks';
import { DeleteColumnButton } from '../components/deleteColumnButton';

interface GenerateTableColumnsProps {
    allocationId: number;
    dimensions: string[];
    onEditMode: boolean;
    handleSavePercentage: (row: any, dimension: string, value: string, prevValue: string, inputRef: RefObject<HTMLInputElement>) => void;
    setIsInputActive: (active: boolean) => void;
}

// Calculates the total percentage of emissions for a site
export const calculateTotalEmissions = (site: Site): number => site.dimensionAllocations.reduce((sum, da) => sum + (da.percentage || 0), 0);

// Generates column definitions for the allocation details table
export const generateTableColumns = ({
    allocationId,
    dimensions,
    onEditMode,
    handleSavePercentage,
    setIsInputActive,
}: GenerateTableColumnsProps): MRT_ColumnDef<Site>[] => {
    const baseColumns: MRT_ColumnDef<Site>[] = [
        {
            accessorKey: 'siteName',
            header: 'Site',
            size: 250,
            muiTableHeadCellProps: {
                sx: {
                    color: COLORS.Mainblue,
                    fontWeight: 500,
                },
            },
        },
        {
            accessorKey: 'type',
            header: 'Type of Site',
            size: 150,
            muiTableHeadCellProps: {
                sx: {
                    color: COLORS.Mainblue,
                    fontWeight: 500,
                },
            },
        },
        {
            accessorKey: 'customerSiteId',
            header: 'Customer Site ID',
            size: 150,
            muiTableHeadCellProps: {
                sx: {
                    color: COLORS.Mainblue,
                    fontWeight: 500,
                },
            },
        },
        {
            accessorKey: 'country',
            header: 'Country',
            size: 150,
            muiTableHeadCellProps: {
                sx: {
                    color: COLORS.Mainblue,
                    fontWeight: 500,
                },
            },
        },
    ];

    const dynamicColumns: MRT_ColumnDef<Site>[] = dimensions.map((dimension, index) => ({
        accessorFn: (row) => {
            const allocation = row.dimensionAllocations.find((da) => da.dimension === dimension);
            return allocation?.percentage || 0;
        },
        header: dimension,
        size: 150,
        Cell: ({ row }: any) => {
            const site = row.original as Site;
            const allocation = site.dimensionAllocations.find((da) => da.dimension === dimension);
            const value = allocation?.percentage?.toLocaleString() || '0';
            const inputRef = React.useRef<HTMLInputElement>(null);

            if (onEditMode) {
                return (
                    <TextField
                        inputRef={inputRef}
                        InputProps={{ endAdornment: <span style={{ marginLeft: '4px' }}>%</span> }}
                        defaultValue={value}
                        onFocus={() => setIsInputActive(true)}
                        onBlur={(e) => {
                            setIsInputActive(false);
                            handleSavePercentage(row, dimension, e.target.value, value, inputRef);
                        }}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                inputRef.current?.blur();
                            }
                        }}
                        variant="standard"
                        sx={{
                            width: '100%',
                            '& input': {
                                textAlign: 'center',
                                padding: '4px',
                                '&:focus': { border: '1px solid #054254', borderRadius: '4px' },
                            },
                        }}
                    />
                );
            }

            return <Box sx={{ textAlign: 'center' }}>{`${value}%`}</Box>;
        },
        muiTableHeadCellProps: {
            sx: { color: COLORS.Mainblue, fontWeight: 500 },
        },
        muiTableBodyCellProps: {
            sx: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'white',
                ...(onEditMode && {
                    borderRight: '1px solid #054254',
                    borderBottom: '1px solid #054254',
                    ...(index === 0 && { borderLeft: '1px solid #054254' }),
                }),
            },
        },
        ...(onEditMode && allocationId && {
            renderColumnActionsMenuItems: ({ internalColumnMenuItems }) => [
                ...internalColumnMenuItems,
                <DeleteColumnButton
                    dimension={dimension}
                    allocationId={allocationId}
                />
            ],
        }),
    }));

    const totalColumn: MRT_ColumnDef<Site> = {
        accessorFn: (row) => calculateTotalEmissions(row),
        header: 'Total',
        size: 150,
        Cell: ({ row }: any) => {
            const total = calculateTotalEmissions(row.original);
            const isInvalid = total !== 0 && total !== 100;
            return <div style={{ color: isInvalid ? '#F20000' : 'black' }}>{total}%</div>;
        },
        muiTableHeadCellProps: { sx: { color: COLORS.Mainblue, fontWeight: 500 } },
        muiTableBodyCellProps: {
            sx: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'rgba(114, 184, 60, 0.05)',
            },
        },
    };

    return [
        { id: 'properties', header: 'Properties', columns: baseColumns },
        { id: 'allocationByDimension', header: '% Allocation by Dimension', columns: [...dynamicColumns, totalColumn] },
    ];
};

// Applies dimension allocation changes to a list of sites
export const applyChangesToSites = (sites: Site[], changes: DimensionAllocation[]): Site[] =>
    sites.map((site) => {
        const siteChanges = changes.filter((change) => change.siteId === site.siteId);
        if (siteChanges.length > 0) {
            const updatedAllocations = site.dimensionAllocations.map((da) => {
                const change = siteChanges.find((c) => c.dimension === da.dimension);
                return change ? { ...da, percentage: change.percentage } : da;
            });
            return { ...site, dimensionAllocations: updatedAllocations };
        }
        return site;
    });

// Validates and saves percentage input for a site's dimension allocation
export const handleSavePercentage = (
    row: MRT_Row<Site>,
    dimension: string,
    value: string,
    prevValue: string,
    inputRef: RefObject<HTMLInputElement>,
    notify: ReturnType<typeof useNotify>
) => {
    const normalizedValue = value.trim().replace(',', '.');

    // If value is empty, set it to 0
    if (normalizedValue === '') {
        if (inputRef.current) {
            // eslint-disable-next-line no-param-reassign
            inputRef.current.value = '0';
        }
        return {
            siteId: row.original.siteId,
            dimension,
            percentage: 0,
        };
    }

    const decimalRegex = /^\d*\.?\d+$/;
    const parsedValue = parseFloat(normalizedValue);
    if (!decimalRegex.test(normalizedValue) || Number.isNaN(parsedValue) || parsedValue < 0 || parsedValue > 100) {
        notify.error('Input must be a numeric value between 0 and 100. Please adjust your entry.');
        if (inputRef.current) {
            // eslint-disable-next-line no-param-reassign
            inputRef.current.value = prevValue;
        }
        return null;
    }

    return {
        siteId: row.original.siteId,
        dimension,
        percentage: parsedValue,
    };
};